import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X, Phone } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/lib/data";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (link) => {
    setOpen(false);
    if (link.to) {
      navigate(link.to);
      return;
    }
    const href = typeof link === "string" ? link : link.href;
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#0A0607]/80 border-b border-[#D4AF37]/20" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-20 md:h-24 flex items-center justify-between">
        <div className="hidden md:flex items-center gap-8 flex-1">
          {NAV_LINKS.slice(0, 3).map((l) => (
            <button
              key={l.label}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => go(l)}
              className="font-body text-xs tracking-[0.2em] uppercase text-[#A89F91] hover:text-[#D4AF37] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </div>

        <button onClick={() => go({ to: "/" })} data-testid="nav-logo" className="flex items-center gap-3 md:flex-1 md:justify-center">
          <img src="/logo.png" alt="Palace of Flavor" className="h-16 md:h-20 w-auto object-contain drop-shadow-[0_2px_10px_rgba(212,175,55,0.25)]" />
        </button>

        <div className="hidden md:flex items-center gap-8 flex-1 justify-end">
          {NAV_LINKS.slice(3).map((l) => (
            <button
              key={l.label}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => go(l)}
              className="font-body text-xs tracking-[0.2em] uppercase text-[#A89F91] hover:text-[#D4AF37] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-book-btn"
            onClick={() => go({ href: "#contact" })}
            className="group relative px-5 py-2.5 border border-[#D4AF37]/50 text-[#D4AF37] text-xs tracking-[0.2em] uppercase overflow-hidden"
          >
            <span className="relative z-10 group-hover:text-[#0A0607] transition-colors duration-300">Book Us</span>
            <span className="absolute inset-0 bg-[#D4AF37] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(true)}
          className="md:hidden text-[#D4AF37]"
          aria-label="Open menu"
        >
          <MenuIcon size={26} />
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#0A0607] flex flex-col"
          >
            <div className="flex justify-between items-center px-6 h-20">
              <img src="/logo.png" alt="Palace of Flavor" className="h-12 w-auto" />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-[#D4AF37]"><X size={28} /></button>
            </div>
            <div className="flex-1 flex flex-col justify-center gap-2 px-8">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.label}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  initial={{ x: -30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.1 + i * 0.07 }}
                  onClick={() => go(l)}
                  className="font-display text-5xl text-left text-[#FDFBF7] hover:text-[#D4AF37] transition-colors py-2"
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
            <a href={CONTACT.phoneHref} className="flex items-center gap-3 px-8 py-6 border-t border-[#D4AF37]/20 text-[#D4AF37]">
              <Phone size={18} /> <span className="font-body tracking-wide">{CONTACT.phone}</span>
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
